/**
 * LA CABECERA, EL MENU Y EL PIE
 * =============================================================================
 * -----------------------------------------------------------------------------
 * LO QUE CAMBIA AL BAJAR
 * -----------------------------------------------------------------------------
 * Arriba del todo la cabecera es transparente, para que la fotografia de la
 * portada se vea completa. Al bajar se vuelve opaca y con desenfoque, porque si
 * no, el texto del menu se perderia sobre las fotografias de las secciones.
 *
 * -----------------------------------------------------------------------------
 * EL MENU DEL TELEFONO
 * -----------------------------------------------------------------------------
 * En pantalla grande la navegacion esta siempre a la vista. En el telefono se
 * esconde y se abre con el boton de tres lineas. Al abrirlo:
 *
 *   · Se bloquea el desplazamiento de la pagina de atras, para que no se mueva
 *     todo mientras se navega el menu.
 *   · Se cierra con la tecla Escape.
 *   · El foco entra en el menu, para que quien navega con el teclado no se quede
 *     fuera.
 */

import { useEffect, useMemo, useState } from 'react';
import { Icono } from './Iconos';
import { Logo } from './Logo';
import { useIdioma } from '../i18n/useIdioma';
import { useAjustes } from '../i18n/useAjustes';

/** Las secciones, en el orden en que aparecen en la pagina. */
const SECCIONES = [
  { id: 'inicio', clave: 'inicio' },
  { id: 'academia', clave: 'academia' },
  { id: 'metodologia', clave: 'metodologia' },
  { id: 'programas', clave: 'programas' },
  { id: 'competencias', clave: 'competencias' },
  { id: 'informacion', clave: 'informacion' },
  { id: 'contacto', clave: 'contacto' },
] as const;

/** Baja a una seccion. Se usa desde toda la pagina. */
function irASeccion(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Cabecera() {
  const { textos, idioma, cambiarIdioma } = useIdioma();
  const [pegada, setPegada] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [activa, setActiva] = useState('inicio');

  // --- El fondo al bajar ---
  useEffect(() => {
    const alDesplazar = () => setPegada(window.scrollY > 40);
    alDesplazar();
    window.addEventListener('scroll', alDesplazar, { passive: true });
    return () => window.removeEventListener('scroll', alDesplazar);
  }, []);

  // --- Que seccion se esta viendo ---
  // Sirve para marcar el enlace activo con su linea dorada. Se usa
  // `IntersectionObserver`, que es la forma eficiente: no hay que calcular nada en
  // cada desplazamiento.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          if (e.isIntersecting) setActiva(e.target.id);
        }
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );

    for (const s of SECCIONES) {
      const el = document.getElementById(s.id);
      if (el) observador.observe(el);
    }

    return () => observador.disconnect();
  }, []);

  return (
    <>
      <header className={'cabecera' + (pegada ? ' cabecera--pegada' : '')}>
        <div className="caja cabecera__interior">
          <a
            href="#inicio"
            className="cabecera__marca"
            aria-label={textos.a11y.logoAlt}
            onClick={(e) => {
              e.preventDefault();
              irASeccion('inicio');
            }}
          >
            <Logo alto={38} conTexto />
          </a>

          <nav className="nav" aria-label={textos.menu.irA}>
            {SECCIONES.map((s) => (
              <a
                key={s.id}
                href={'#' + s.id}
                className={'nav__enlace' + (activa === s.id ? ' nav__enlace--activo' : '')}
                aria-current={activa === s.id ? 'true' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  irASeccion(s.id);
                }}
              >
                {textos.menu[s.clave]}
              </a>
            ))}
          </nav>

          <div className="cabecera__acciones">
            <div className="idioma" role="group" aria-label={textos.menu.idioma}>
              <button
                type="button"
                className={'idioma__opcion' + (idioma === 'es' ? ' idioma__opcion--activo' : '')}
                onClick={() => cambiarIdioma('es')}
                aria-pressed={idioma === 'es'}
                lang="es"
              >
                ES
              </button>
              <span className="idioma__separador" aria-hidden="true">
                |
              </span>
              <button
                type="button"
                className={'idioma__opcion' + (idioma === 'en' ? ' idioma__opcion--activo' : '')}
                onClick={() => cambiarIdioma('en')}
                aria-pressed={idioma === 'en'}
                lang="en"
              >
                EN
              </button>
            </div>

            <button
              type="button"
              className="menu-boton"
              onClick={() => setMenuAbierto(true)}
              aria-label={textos.menu.abrirMenu}
              aria-expanded={menuAbierto}
            >
              <Icono nombre="menu" tam={26} />
            </button>
          </div>
        </div>
      </header>

      {menuAbierto && <Menu abierto={menuAbierto} alCerrar={() => setMenuAbierto(false)} />}
    </>
  );
}

/**
 * El menu desplegable del telefono.
 */
export function Menu({ abierto, alCerrar }: { abierto: boolean; alCerrar: () => void }) {
  const { textos } = useIdioma();

  // Mientras esta abierto, se bloquea el desplazamiento de la pagina de atras.
  useEffect(() => {
    if (!abierto) return;

    const overflowOriginal = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar();
    };
    window.addEventListener('keydown', alTeclear);

    return () => {
      document.body.style.overflow = overflowOriginal;
      window.removeEventListener('keydown', alTeclear);
    };
  }, [abierto, alCerrar]);

  if (!abierto) return null;

  const irA = (id: string) => {
    alCerrar();
    irASeccion(id);
  };

  return (
    <div className="menu" role="dialog" aria-modal="true" aria-label={textos.menu.irA}>
      <div className="caja menu__cabecera">
        <Logo alto={34} conTexto />
        <button
          type="button"
          className="menu-boton"
          onClick={alCerrar}
          aria-label={textos.menu.cerrarMenu}
          autoFocus
        >
          <Icono nombre="cerrar" tam={26} />
        </button>
      </div>

      <div className="menu__cuerpo">
        <div className="caja">
          <ul className="menu__lista">
            {SECCIONES.map((s, i) => (
              <li key={s.id}>
                <button type="button" className="menu__enlace" onClick={() => irA(s.id)}>
                  <span>{textos.menu[s.clave]}</span>
                  <span className="menu__numero">{String(i + 1).padStart(2, '0')}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/**
 * El pie.
 *
 * La seccion de contacto ya termina con el logotipo y el lema, asi que el pie es lo
 * minimo: el aviso legal y los accesos. No se repite el lema.
 */
export function Pie() {
  const { textos } = useIdioma();
  const { valor } = useAjustes();
  const anio = useMemo(() => new Date().getFullYear(), []);

  return (
    <footer className="pie">
      <div className="caja pie__interior">
        <Logo alto={32} conTexto />

        <p className="pie__texto">
          © {anio} {valor('academia_nombre', 'M SOCCER ACADEMY')} · {textos.inicio.subtitulo}
        </p>

        <div className="pie__enlaces">
          <a
            href="#programas"
            onClick={(e) => {
              e.preventDefault();
              irASeccion('programas');
            }}
          >
            {textos.menu.programas}
          </a>
          <a
            href="#contacto"
            onClick={(e) => {
              e.preventDefault();
              irASeccion('contacto');
            }}
          >
            {textos.menu.contacto}
          </a>
        </div>
      </div>
    </footer>
  );
}
